import { appConfig } from 'config/environment/appConfig'

import { HttpService } from 'services/HttpService'
import { getQueryUrl } from 'services/utils/getQueryUrl'
import { TokensDTO } from 'sections/LKSection/shared/types/types'
import convertKeysToCamelCase from 'utils/helpers/convertKeysToCamelCase'
import { Country, ResponseListData } from 'shared/types/types'

import {
  CompanyDTO,
  CompanyInfoDTO,
  GetDocumentParams,
  GetDocumentResponse,
  LoginByPhoneData,
  OfferSignParams,
  OfferVersionResponse,
  RegisterOwnerBody,
  SendSmsCodeData,
  UpdateCompanyDataDTO,
  UpdateOwnerBody,
  UrlType,
  UserInfoDTO,
} from './types'

const httpService = new HttpService(appConfig.API_URL)
const discoveryHttpService = new HttpService(appConfig.DISCOVERY_URL)
const authHttpService = new HttpService(appConfig.AUTH_API_URL)
const offerHttpService = new HttpService(appConfig.OFFER_URL)

export const userApi = {
  loginByPhone: async (data: LoginByPhoneData): Promise<{ data: string; code?: string; code_length: number }> => {
    return await authHttpService.post('/api/v1/login', {
      body: {
        phone: data.phone,
        resend: data.resend,
        device_type: data.deviceType,
        country: data.country,
        device_uuid: data.deviceUuid,
      },
    })
  },

  sendSmsCode: async (data: SendSmsCodeData): Promise<TokensDTO> => {
    return authHttpService.post('/api/v1/validate', {
      body: {
        token: data.token,
        code: data.code,
        device_token: data.deviceToken,
        device_type: data.deviceType,
        device_uuid: data.deviceUuid,
        alian_device: data.alianDevice,
        device_name: data.deviceName,
        app_version: data.appVersion,
      },
    })
  },

  registerOwner: async (body: RegisterOwnerBody) => {
    await discoveryHttpService.post('/api/v3/owner/register', {
      body,
    })
  },

  getUser: async (): Promise<ResponseListData<UserInfoDTO>> => {
    return await httpService.get('/api/v2/user')
  },

  getCompanies: async (): Promise<CompanyDTO[]> => {
    const result = await discoveryHttpService.get<ResponseListData<CompanyDTO[]>>('/api/v3/pickpoint/owner/info')
    return result?.data ? convertKeysToCamelCase(result.data) : undefined
  },

  getCompanyInfo: async (param: string, type: UrlType): Promise<CompanyInfoDTO> => {
    const url = 'https://suggestions.dadata.ru/suggestions/api/4_1/rs/findById/' + type
    const token = '6aa6c4610172cd3ac53226f64516c1b6969ed7df'

    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      body: JSON.stringify({ query: param }),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: 'Token ' + token,
      },
    })

    if (type === UrlType.bank && !response.ok) {
      throw new Error('')
    }

    return await response.json()
  },

  getOfferVersion: async (countryСode?: Country): Promise<OfferVersionResponse> => {
    const queryUrl = getQueryUrl({
      country_code: countryСode,
    })
    const result = await offerHttpService.get(`/api/v3/offer?${queryUrl}`)

    return convertKeysToCamelCase(result)
  },

  signOfferVersion: async ({ version, countryCode }: OfferSignParams): Promise<{ success: boolean }> => {
    return await offerHttpService.post('/api/v2/sign', {
      body: {
        version,
        country_code: countryCode,
      },
    })
  },

  getDocument: async ({ userId, docType, citizenship }: GetDocumentParams): Promise<GetDocumentResponse> => {
    const result = await discoveryHttpService.get(
      `/api/v4/owner/${userId}/document?doc_type=${docType}&citizenship=CITIZEN_${citizenship}`,
    )
    return convertKeysToCamelCase(result)
  },

  updateOwner: async (body: UpdateOwnerBody): Promise<{ data: true }> => {
    return await discoveryHttpService.post('/api/v3/owner/update', {
      body,
    })
  },

  updateCompany: async (data: Partial<UpdateCompanyDataDTO>): Promise<{ data: boolean }> => {
    const patch = {
      id: data.id,
      bik: data.bik,
      email: data.email,
      inn: data.inn,
      kpp: data.kpp,
      ogrn: data.ogrn,
      okopf: data.okopf,
      okpo: data.okpo,
      org_name: data.orgName,
      org_type: data.orgType,

      tax_type: data.taxType,
      tax_files: data.taxFiles,

      pay_account: data.payAccount,
      country: data.country,
      bank_inn: data.bankInn,
      cor_account: data.corAccount,
      external_ad: data.externalAd,
      bank_name: data.bankName,
      bank_code: data.bankCode,
      beneficiary_pay_account: data.beneficiaryPayAccount,
      local_pay_account: data.localPayAccount,
      local_bik: data.localBik,

      identification_number: data.identification_number,
      passport: data.passport,
      date_of_issue: data.date_of_issue,
      department_code: data.department_code,
      passport_urls: data.passport_urls,
      gender: data.gender,
      date_of_birthday: data.dateOfBirthday,
      place_of_birth: data.placeOfBirth,
      passport_issued: data.passportIssued,
      registration_address: data.registrationAddress,
      permanent_residence_address: data.permanentResidenceAddress,
    }

    const result = await discoveryHttpService.post('/api/v3/pickpoint/owner/update', {
      body: { ...patch },
    })

    return convertKeysToCamelCase(result)
  },

  useAuthHash: async (authHash: string): Promise<TokensDTO> => {
    return await authHttpService.post<TokensDTO>('/api/v1/hash/use', {
      body: {
        token: authHash,
      },
    })
  },
}
